// src/redux/services/remoteService.ts
import { createAsyncThunk } from '@reduxjs/toolkit';
import { axiosInstance } from '../../utils/common';
import type { ModalConfigResponse } from '../types/remoteCurrentTypes';

export interface PayloadItem {
  id: number;
  customer_name: string;
  site_id: string;
  generator_name?: string; // Optional generator_name field
  topic: string;
  payload: string | {
    key: string;
    mid: number;
    src: string;
    value: any;
  };
  payload_key?: string; // Optional flattened payload fields
  payload_value?: any;
  timestamp: string;
}

export interface FetchAllTopicsResponse {
  customers_data: FetchTopicsResponse[];
}

export interface FetchTopicsResponse {
  customer_name: string;
  site_id: string;
  mode?: string;
  total_records?: number;
  payloads: PayloadItem[];
}

// New API response format
export interface NewApiCustomerData {
  customer: string;
  data: PayloadItem[];
}

export interface ParsedPayloadData {
  key: string;
  mid: number;
  src: string;
  value: any;
}

export interface FetchTopicsResult {
  topics: string[];
  siteId: string;
  initialTopicData: Record<string, any>;
}

// Helper function to extract device ID from src field (matching MQTT behavior)
const extractDeviceId = (src: string): string => {
  if (src.includes(':')) {
    const parts = src.split(':');
    if (parts.length >= 5) {
      return parts[4];
    }
  }
  if (src.includes('-')) {
    const parts = src.split('-');
    if (parts.length >= 3) {
      return parts[2];
    }
  }
  return src;
};

// Helper function to parse topic path (matching MQTT behavior)
const parseTopicPath = (topic: string) => {
  const parts = topic.split('/');
  if (parts.length >= 3) {
    const messageType = parts[parts.length - 1];
    const site = parts[parts.length - 2];
    const customer = parts[parts.length - 3];

    return { customer, site, messageType };
  }
  return null;
};

// Async thunk to fetch available MQTT topics for selective subscription
export const fetchAvailableTopics = createAsyncThunk<FetchTopicsResult, { selectedCustomerName: string, siteId?: number }, { rejectValue: string }>(
  'remoteV2/fetchAvailableTopics',
  async ({ selectedCustomerName, siteId }, thunkAPI) => {
    try {
      const response = await axiosInstance.get<NewApiCustomerData[]>(
        siteId
          ? `/telemetry/list_latest_payloads/?customer_name=${selectedCustomerName}&site_id=${siteId}`
          : `/telemetry/list_latest_payloads/?customer_name=${selectedCustomerName}`
      );

      // New API format: [{ customer: string, data: PayloadItem[] }]
      const customerData = response.data.find((item: NewApiCustomerData) =>
        item.customer.toLowerCase() === selectedCustomerName.toLowerCase()
      );

      if (!customerData || !customerData.data) {
        return {
          topics: [],
          siteId: '',
          initialTopicData: {}
        };
      }

      const payloadsArray = customerData.data;
      // Extract site_id from first item in data array
      const extractedSiteId = customerData.data[0]?.site_id || '';

      // Extract unique topics from the payloads array
      const uniqueTopics = Array.from(new Set(payloadsArray.map(payload => payload.topic)));

      // Transform payloads into the same structure as MQTT data
      const initialTopicData: Record<string, any> = {};

      payloadsArray.forEach(item => {
        try {
          // Handle both string and object payload formats
          const parsedPayload: ParsedPayloadData = typeof item.payload === 'string'
            ? JSON.parse(item.payload)
            : item.payload;

          // Parse topic to get customer, site, messageType
          const parsedTopic = parseTopicPath(item.topic);
          if (!parsedTopic) return;

          const { customer, site, messageType } = parsedTopic;

          // Use generator_name as device ID (with fallback to extracting from src)
          const deviceId = item.generator_name || extractDeviceId(parsedPayload.src);

          // Use customer/site combination as key (matching MQTT structure)
          const customerSiteKey = `${customer}/${site}`;

          // Initialize topic data if it doesn't exist
          if (!initialTopicData[customerSiteKey]) {
            initialTopicData[customerSiteKey] = {
              customer,
              site,
              messageType,
              devices: {}
            };
          }

          // Initialize device data if it doesn't exist
          if (!initialTopicData[customerSiteKey].devices[deviceId]) {
            initialTopicData[customerSiteKey].devices[deviceId] = {
              deviceId,
              sensorHistory: {}
            };
          }

          // Add sensor reading (matching MQTT structure)
          const sensorKey = parsedPayload.key;
          const timestamp = new Date(item.timestamp).getTime();
          const formattedTime = new Date(timestamp).toLocaleTimeString();

          const sensorReading = {
            value: parsedPayload.value,
            mid: parsedPayload.mid,
            src: parsedPayload.src,
            timestamp,
            formattedTime
          };

          // Initialize sensor history array if it doesn't exist
          if (!initialTopicData[customerSiteKey].devices[deviceId].sensorHistory[sensorKey]) {
            initialTopicData[customerSiteKey].devices[deviceId].sensorHistory[sensorKey] = [];
          }

          // Add the sensor reading
          initialTopicData[customerSiteKey].devices[deviceId].sensorHistory[sensorKey].push(sensorReading);

        } catch (parseError) {
          console.error('Failed to process payload:', item.payload, parseError);
        }
      });

      return {
        topics: uniqueTopics,
        siteId: extractedSiteId,
        initialTopicData
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || 'Failed to fetch topics');
    }
  }
);

export const fetchAllAvailableTopicsForPioneer = createAsyncThunk<FetchTopicsResult, void, { rejectValue: string }>(
  'remoteV2/fetchAllAvailableTopicsForPioneer',
  async (_, thunkAPI) => {
    try {
      const response = await axiosInstance.get<NewApiCustomerData[]>(`/telemetry/list_latest_payloads/`);

      // New API format: [{ customer: string, data: PayloadItem[] }]
      const allPayloads: PayloadItem[] = [];
      let extractedSiteId: string = '';

      response.data.forEach((customerData: NewApiCustomerData) => {
        if (customerData.data && customerData.data.length > 0) {
          allPayloads.push(...customerData.data);
          // Extract site_id from first non-empty data array if not already set
          if (!extractedSiteId && customerData.data[0]?.site_id) {
            extractedSiteId = customerData.data[0].site_id;
          }
        }
      });

      // Extract unique topics from all payloads across customers
      const uniqueTopics = Array.from(new Set(allPayloads.map(payload => payload.topic)));

      // Transform payloads into the same structure as MQTT data
      const initialTopicData: Record<string, any> = {};

      // Process all payloads
      allPayloads.forEach(item => {
        try {
          // Handle both string and object payload formats
          const parsedPayload: ParsedPayloadData = typeof item.payload === 'string'
            ? JSON.parse(item.payload)
            : item.payload;

          // Parse topic to get customer, site, messageType
          const parsedTopic = parseTopicPath(item.topic);
          if (!parsedTopic) return;

          const { customer: topicCustomer, site, messageType } = parsedTopic;

          // Use customer_name from item if available, else fallback to parsed topic customer
          const customerName = item.customer_name || topicCustomer;

          // Use generator_name as device ID (with fallback to extracting from src)
          const deviceId = item.generator_name || extractDeviceId(parsedPayload.src);

          // Use customer/site combination as key (matching MQTT structure)
          const customerSiteKey = `${customerName}/${site}`;

          // Initialize topic data if it doesn't exist
          if (!initialTopicData[customerSiteKey]) {
            initialTopicData[customerSiteKey] = {
              customer: customerName,
              site,
              messageType,
              devices: {}
            };
          }

          // Initialize device data if it doesn't exist
          if (!initialTopicData[customerSiteKey].devices[deviceId]) {
            initialTopicData[customerSiteKey].devices[deviceId] = {
              deviceId,
              sensorHistory: {}
            };
          }

          // Add sensor reading (matching MQTT structure)
          const sensorKey = parsedPayload.key;
          const timestamp = new Date(item.timestamp).getTime();
          const formattedTime = new Date(timestamp).toLocaleTimeString();

          const sensorReading = {
            value: parsedPayload.value,
            mid: parsedPayload.mid,
            src: parsedPayload.src,
            timestamp,
            formattedTime
          };

          // Initialize sensor history array if it doesn't exist
          if (!initialTopicData[customerSiteKey].devices[deviceId].sensorHistory[sensorKey]) {
            initialTopicData[customerSiteKey].devices[deviceId].sensorHistory[sensorKey] = [];
          }

          // Add the sensor reading
          initialTopicData[customerSiteKey].devices[deviceId].sensorHistory[sensorKey].push(sensorReading);

        } catch (parseError) {
          console.error('Failed to process payload:', item.payload, parseError);
        }
      });

      return {
        topics: uniqueTopics,
        siteId: extractedSiteId,
        initialTopicData
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || 'Failed to fetch topics');
    }
  }
);

// Fetch modal configuration from API
export const fetchModalConfig = createAsyncThunk<
  ModalConfigResponse,
  void,
  {
    rejectValue: string;
  }
>("remoteV2/fetchModalConfig", async (_, thunkAPI) => {
  try {
    const { data } = await axiosInstance.get<ModalConfigResponse>(
      "/telemetry/modalConfig/"
    );
    return data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || "Failed to fetch modal configuration"
    );
  }
});